/**
 * Lemai Alarm Popup System
 *
 * Critical alarms (fire, duress) pop up as a full-screen modal in the center
 * of the screen. The associated camera feed is displayed alongside the alarm.
 * Banner-level alarms appear as a sliding notification in the top-right corner.
 *
 * Usage: <AlarmPopupProvider> wraps the app. Any component can call
 *   useAlarmPopup().triggerAlarm(alarm) to fire an alarm.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AlarmSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AlarmDisplayMode = 'banner' | 'popup';

export interface AlarmEvent {
  id: string;
  type: string;
  title: string;
  site: string;
  location: string;
  severity: AlarmSeverity;
  displayMode: AlarmDisplayMode;
  cameraId?: string;
  cameraLabel?: string;
  snapshotUrl?: string;
  timestamp: Date;
  sop?: string[];
  jobNumber?: string;
}

interface AlarmContextValue {
  triggerAlarm: (alarm: AlarmEvent) => void;
  dismissAlarm: (id: string) => void;
  acknowledgeAlarm: (id: string) => void;
  pendingAlarms: AlarmEvent[];
  settings: AlarmSettings;
  updateSettings: (s: Partial<AlarmSettings>) => void;
}

export interface AlarmSettings {
  // Per-alarm-type override: 'banner' | 'popup' | 'default'
  typeOverrides: Record<string, AlarmDisplayMode>;
  autoPopupTypes: string[];   // always popup for these types
  autoDismissSeconds: number; // 0 = no auto-dismiss
  soundEnabled: boolean;
}

const DEFAULT_SETTINGS: AlarmSettings = {
  typeOverrides: {},
  autoPopupTypes: ['Duress Button', 'Fire Panel Input', 'Panic Button', 'Armed Hold-up'],
  autoDismissSeconds: 0,
  soundEnabled: false,
};

const AlarmContext = createContext<AlarmContextValue | null>(null);

export function useAlarmPopup() {
  const ctx = useContext(AlarmContext);
  if (!ctx) throw new Error('useAlarmPopup must be used within AlarmPopupProvider');
  return ctx;
}

// ─── Severity helpers ─────────────────────────────────────────────────────────

const SEV_COLOR: Record<AlarmSeverity, string> = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#dc2626',
};

const SEV_BG: Record<AlarmSeverity, string> = {
  low: 'rgba(34,197,94,0.12)',
  medium: 'rgba(245,158,11,0.12)',
  high: 'rgba(239,68,68,0.15)',
  critical: 'rgba(220,38,38,0.2)',
};

const ALARM_ICONS: Record<string, string> = {
  'Duress Button': '🆘',
  'Fire Panel Input': '🔥',
  'Panic Button': '🆘',
  'Armed Hold-up': '⚠️',
  'Door Forced Open': '🚪',
  'Perimeter Breach': '🚨',
  'Camera Tamper': '📷',
  'Glass Break': '🪟',
  'Motion Detected': '💨',
  'default': '🚨',
};

// ─── Camera Feed ──────────────────────────────────────────────────────────────

function CameraFeed({ snapshotUrl, label }: { snapshotUrl?: string; label?: string }) {
  if (!snapshotUrl) {
    return (
      <div style={{ width:'100%', height:220, background:'rgba(0,0,0,0.6)', borderRadius:8, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize:32, marginBottom:8 }}>📷</div>
        <div style={{ fontSize:12, color:'#555555' }}>{label || 'Camera feed not available'}</div>
      </div>
    );
  }
  return (
    <div style={{ width:'100%', borderRadius:8, overflow:'hidden', border:'1px solid rgba(255,255,255,0.12)', position:'relative' }}>
      <img src={snapshotUrl} alt={label} style={{ width:'100%', height:220, objectFit:'cover', display:'block' }}/>
      <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'6px 10px', background:'linear-gradient(transparent, rgba(0,0,0,0.8))', fontSize:11, color:'#e2e8f0', fontWeight:600 }}>
        📷 {label || 'Alarm Camera'}
        <span style={{ float:'right', color:'#ef4444', fontWeight:700 }}>● LIVE</span>
      </div>
    </div>
  );
}

// ─── Full-screen Popup Modal ──────────────────────────────────────────────────

function AlarmPopupModal({ alarm, onAcknowledge, onDismiss }: {
  alarm: AlarmEvent;
  onAcknowledge: () => void;
  onDismiss: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const color = SEV_COLOR[alarm.severity];
  const bg = SEV_BG[alarm.severity];
  const icon = ALARM_ICONS[alarm.type] || ALARM_ICONS.default;
  const elapsedStr = elapsed < 60 ? `${elapsed}s ago` : `${Math.floor(elapsed/60)}m ${elapsed%60}s ago`;

  return ReactDOM.createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:'rgba(0,0,0,0.85)', backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }}>
      {/* Pulsing border */}
      <div style={{
        position:'absolute', inset:0, border:`3px solid ${color}`,
        animation:'alarmPulse 1s ease-in-out infinite', pointerEvents:'none', zIndex:100000,
      }}/>

      <div style={{
        background:'#0d0d0d', border:`2px solid ${color}`,
        borderRadius:16, padding:'28px 32px', maxWidth:780, width:'100%',
        boxShadow:`0 0 60px ${color}60, 0 24px 80px rgba(0,0,0,0.8)`,
        position:'relative', maxHeight:'90vh', overflowY:'auto',
      }}>
        {/* Header */}
        <div style={{ display:'flex', gap:16, alignItems:'flex-start', marginBottom:20 }}>
          <div style={{ width:64, height:64, borderRadius:12, background:bg, border:`2px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, flexShrink:0 }}>
            {icon}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color, marginBottom:4 }}>
              {alarm.severity} ALARM — IMMEDIATE RESPONSE REQUIRED
            </div>
            <div style={{ fontSize:24, fontWeight:800, color:'#f1f5f9', lineHeight:1.2 }}>{alarm.title}</div>
            <div style={{ fontSize:14, color:'#94a3b8', marginTop:6 }}>
              📍 {alarm.site} — {alarm.location}
              <span style={{ marginLeft:16, color:'#555555' }}>⏱ {elapsedStr}</span>
              {alarm.jobNumber && <span style={{ marginLeft:16, color:'#3b82f6' }}>Job #{alarm.jobNumber}</span>}
            </div>
          </div>
        </div>

        {/* Two-column: camera + SOP */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          {/* Camera feed */}
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Associated Camera</div>
            <CameraFeed snapshotUrl={alarm.snapshotUrl} label={alarm.cameraLabel}/>
          </div>

          {/* SOP */}
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Standard Operating Procedure</div>
            {alarm.sop && alarm.sop.length > 0 ? (
              <ol style={{ margin:0, padding:'0 0 0 16px', listStyle:'decimal' }}>
                {alarm.sop.map((step, i) => (
                  <li key={i} style={{ fontSize:13, color:'#cbd5e1', marginBottom:8, lineHeight:1.5 }}>
                    {step}
                  </li>
                ))}
              </ol>
            ) : (
              <div style={{ fontSize:13, color:'#555555', fontStyle:'italic' }}>No SOP defined for this alarm type. Contact your supervisor.</div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onDismiss} style={{ padding:'10px 20px', borderRadius:8, border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.05)', color:'#94a3b8', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Dismiss
          </button>
          <button onClick={onAcknowledge} style={{ padding:'10px 28px', borderRadius:8, border:'none', background:color, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', boxShadow:`0 4px 16px ${color}60` }}>
            ✓ Acknowledge Alarm
          </button>
        </div>
      </div>

      <style>{`
        @keyframes alarmPulse {
          0%,100% { opacity:0.6; }
          50% { opacity:1; }
        }
      `}</style>
    </div>,
    document.body
  );
}

// ─── Banner Notification ──────────────────────────────────────────────────────

function AlarmBanner({ alarm, onAcknowledge, onDismiss }: {
  alarm: AlarmEvent;
  onAcknowledge: () => void;
  onDismiss: () => void;
}) {
  const color = SEV_COLOR[alarm.severity];
  const icon = ALARM_ICONS[alarm.type] || ALARM_ICONS.default;

  return (
    <div style={{
      background:'#0a0a0a', border:`1px solid ${color}`,
      borderLeft:`4px solid ${color}`, borderRadius:10,
      padding:'12px 16px', marginBottom:8, minWidth:320, maxWidth:380,
      boxShadow:`0 4px 24px rgba(0,0,0,0.6), 0 0 12px ${color}30`,
      animation:'bannerSlideIn 0.3s ease-out',
    }}>
      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
        <span style={{ fontSize:20, flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#f1f5f9' }}>{alarm.title}</div>
            <button onClick={onDismiss} style={{ background:'none', border:'none', color:'#555555', cursor:'pointer', fontSize:16, lineHeight:1, padding:0, marginLeft:8, flexShrink:0 }}>×</button>
          </div>
          <div style={{ fontSize:11, color:'#555555', marginTop:2 }}>{alarm.site} — {alarm.location}</div>
          <div style={{ fontSize:10, color:color, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, marginTop:4 }}>{alarm.severity}</div>
        </div>
      </div>
      <div style={{ display:'flex', gap:6, marginTop:10 }}>
        <button onClick={onAcknowledge} style={{ flex:1, padding:'6px', borderRadius:6, border:'none', background:color, color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>Acknowledge</button>
        <button onClick={onDismiss} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.04)', color:'#555555', fontSize:11, cursor:'pointer' }}>Dismiss</button>
      </div>
      <style>{`
        @keyframes bannerSlideIn {
          from { opacity:0; transform:translateX(40px); }
          to { opacity:1; transform:translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AlarmPopupProvider({ children }: { children: React.ReactNode }) {
  const [alarms, setAlarms] = useState<AlarmEvent[]>([]);
  const [settings, setSettings] = useState<AlarmSettings>(DEFAULT_SETTINGS);

  function getDisplayMode(alarm: AlarmEvent): AlarmDisplayMode {
    if (settings.autoPopupTypes.includes(alarm.type)) return 'popup';
    if (settings.typeOverrides[alarm.type]) return settings.typeOverrides[alarm.type];
    return alarm.displayMode;
  }

  const triggerAlarm = useCallback((alarm: AlarmEvent) => {
    setAlarms(prev => {
      if (prev.some(a => a.id === alarm.id)) return prev;
      return [...prev, { ...alarm, displayMode: settings.autoPopupTypes.includes(alarm.type) ? 'popup' : alarm.displayMode }];
    });
  }, [settings]);

  const dismissAlarm = useCallback((id: string) => {
    setAlarms(prev => prev.filter(a => a.id !== id));
  }, []);

  const acknowledgeAlarm = useCallback((id: string) => {
    setAlarms(prev => prev.filter(a => a.id !== id));
  }, []);

  const updateSettings = useCallback((s: Partial<AlarmSettings>) => {
    setSettings(prev => ({ ...prev, ...s }));
  }, []);

  // Popup alarms — shown as modal, highest priority first
  const popupAlarms = alarms.filter(a => getDisplayMode(a) === 'popup');
  const bannerAlarms = alarms.filter(a => getDisplayMode(a) === 'banner');

  // Show only the highest-severity popup at once
  const activePopup = popupAlarms.sort((a, b) => {
    const order: Record<AlarmSeverity, number> = { critical:4, high:3, medium:2, low:1 };
    return order[b.severity] - order[a.severity];
  })[0];

  return (
    <AlarmContext.Provider value={{ triggerAlarm, dismissAlarm, acknowledgeAlarm, pendingAlarms: alarms, settings, updateSettings }}>
      {children}

      {/* Popup modal */}
      {activePopup && (
        <AlarmPopupModal
          alarm={activePopup}
          onAcknowledge={() => acknowledgeAlarm(activePopup.id)}
          onDismiss={() => dismissAlarm(activePopup.id)}
        />
      )}

      {/* Banner notifications — stacked top-right */}
      {bannerAlarms.length > 0 && ReactDOM.createPortal(
        <div style={{ position:'fixed', top:80, right:20, zIndex:9998, display:'flex', flexDirection:'column', maxWidth:400 }}>
          {bannerAlarms.map(a => (
            <AlarmBanner
              key={a.id}
              alarm={a}
              onAcknowledge={() => acknowledgeAlarm(a.id)}
              onDismiss={() => dismissAlarm(a.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </AlarmContext.Provider>
  );
}

// ─── Notification badge count helper ─────────────────────────────────────────

export function useAlarmBadge() {
  const { pendingAlarms } = useAlarmPopup();
  return pendingAlarms.length;
}

// ─── Alarm Settings Panel (used inside Settings module) ──────────────────────

export function AlarmSettingsPanel() {
  const { settings, updateSettings } = useAlarmPopup();
  const ALARM_TYPES = [
    'Duress Button', 'Fire Panel Input', 'Panic Button', 'Armed Hold-up',
    'Door Forced Open', 'Perimeter Breach', 'Camera Tamper', 'Glass Break',
    'Motion Detected', 'Power Failure', 'Loitering',
  ];

  function toggleAutoPopup(type: string) {
    const current = settings.autoPopupTypes;
    updateSettings({
      autoPopupTypes: current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type],
    });
  }

  return (
    <div>
      <div style={{ fontSize:13, color:'#555555', marginBottom:16 }}>
        Choose how each alarm type is displayed. Pop-up alarms appear in the center of the screen — use for life-safety events that operators must not miss.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        {ALARM_TYPES.map(type => {
          const isPopup = settings.autoPopupTypes.includes(type);
          const icon = ALARM_ICONS[type] || '🚨';
          return (
            <div key={type} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8 }}>
              <span style={{ fontSize:18 }}>{icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, color:'#e2e8f0' }}>{type}</div>
                <div style={{ fontSize:11, color: isPopup?'#f59e0b':'#555555' }}>
                  {isPopup ? '⚡ Full-screen pop-up' : '📌 Banner notification'}
                </div>
              </div>
              <div onClick={() => toggleAutoPopup(type)} style={{ width:44, height:24, borderRadius:12, background: isPopup?'#f59e0b':'#111111', border:'1px solid '+(isPopup?'#f59e0b':'#222222'), cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:3, left: isPopup?22:3, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.2s' }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
